import React, { useCallback, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useLanguage } from '../../contexts/LanguageContext';

const getFileIcon = (fileName) => {
    const extension = fileName.toLowerCase().split('.').pop();
    switch (extension) {
        case 'pdf': return 'pi pi-file-pdf';
        case 'doc':
        case 'docx': return 'pi pi-file-word';
        case 'xls':
        case 'xlsx': return 'pi pi-file-excel';
        case 'jpg':
        case 'jpeg':
        case 'png':
        case 'gif': return 'pi pi-image';
        case 'zip':
        case 'rar': return 'pi pi-file-archive';
        case 'msg':
        case 'eml': return 'pi pi-envelope';
        default: return 'pi pi-file';
    }
};

const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const DEFAULT_ACCEPTED_FILE_TYPES = {
    'image/*': ['.png', '.jpg', '.jpeg', '.gif'],
    'application/pdf': ['.pdf'],
    'application/msword': ['.doc'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'application/vnd.ms-excel': ['.xls'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    'text/plain': ['.txt'],
    'application/vnd.ms-outlook': ['.msg'],
    'message/rfc822': ['.eml']
};

const GenericFileUpload = ({
    newFiles = [],
    onFilesChange,
    existingDocuments = [],
    onDownloadDocument,
    onDeleteExistingDocument,
    onUpdateExistingDocument,
    isLoading = false,
    maxFileSize = 20971520,
    disabled = false,
    multiple = true,
    acceptedFileTypes = DEFAULT_ACCEPTED_FILE_TYPES,
    showDownloadButton = true,
    showUpdateButton = true,
    showDeleteButton = true,
    readOnlyMode = false
}) => {
    const { th } = useLanguage();
    const toast = useRef(null);

    const onDrop = useCallback((acceptedFiles) => {
        if (readOnlyMode) return;

        const filesToProcess = multiple ? acceptedFiles : [acceptedFiles[0]].filter(Boolean);

        const validFiles = filesToProcess.filter(file => {
            if (file.size > maxFileSize) {
                toast.current?.show({
                    severity: 'error',
                    summary: th('fileUpload.fileSizeError'),
                    detail: th('fileUpload.fileTooLarge', { fileName: file.name, maxSize: maxFileSize / 1024 / 1024 }),
                    life: 4000
                });
                return false;
            }
            return true;
        });

        if (validFiles.length > 0) {
            if (multiple) {
                onFilesChange([...newFiles, ...validFiles]);
            } else {
                onFilesChange(validFiles);

                if (acceptedFiles.length > 1) {
                    toast.current?.show({
                        severity: 'warn',
                        summary: th('fileUpload.singleFileSelection'),
                        detail: th('fileUpload.singleFileSelectionDetail'),
                        life: 3000
                    });
                }
            }

            toast.current?.show({
                severity: 'success',
                summary: th('fileUpload.fileSelected'),
                detail: th('fileUpload.filesSelectedDetail', { count: validFiles.length }),
                life: 3000
            });
        }
    }, [newFiles, onFilesChange, maxFileSize, multiple, readOnlyMode, th]);

    const { getRootProps, getInputProps } = useDropzone({
        onDrop,
        multiple: multiple,
        accept: acceptedFileTypes,
        disabled: disabled || readOnlyMode
    });

    const removeNewFile = (indexToRemove) => {
        if (readOnlyMode) return;
        const updatedFiles = newFiles.filter((_, index) => index !== indexToRemove);
        onFilesChange(updatedFiles);
    };

    const fileInputRef = useRef(null);

    const handleUpdateDocument = (doc, event) => {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        if (readOnlyMode || !onUpdateExistingDocument) return;
        fileInputRef.current?.click();
        fileInputRef.current.setAttribute('data-doc-id', doc.id);
        fileInputRef.current.setAttribute('data-doc-filename', doc.fileName);
    };

    const handleFileInputChange = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const docId = event.target.getAttribute('data-doc-id');
        const originalFileName = event.target.getAttribute('data-doc-filename');

        if (file.size > maxFileSize) {
            toast.current?.show({
                severity: 'error',
                summary: th('fileUpload.fileSizeError'),
                detail: th('fileUpload.selectedFileTooLarge', { maxSize: maxFileSize / 1024 / 1024 }),
                life: 4000
            });
            return;
        }

        const documentData = {
            id: docId,
            fileName: originalFileName,
            newFile: file
        };

        if (onUpdateExistingDocument) {
            onUpdateExistingDocument(documentData);
        }

        event.target.value = '';
        event.target.removeAttribute('data-doc-id');
        event.target.removeAttribute('data-doc-filename');
    };

    const handleDownloadDocument = (doc, event) => {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        if (onDownloadDocument) {
            onDownloadDocument(doc);
        }
    };

    const handleDeleteDocument = (doc, event) => {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        if (onDeleteExistingDocument) {
            onDeleteExistingDocument(doc);
        }
    };

    const dropzoneMessage = multiple
        ? th('fileUpload.dropzoneMessageMultiple')
        : th('fileUpload.dropzoneMessageSingle');

    const shouldShowUploadArea = !disabled && !readOnlyMode;

    const getSupportedFormatsText = () => {
        const allExtensions = Object.values(acceptedFileTypes).flat();
        return allExtensions.join(', ');
    };

    return (
        <>
            <Toast ref={toast} />

            <input
                ref={fileInputRef}
                type="file"
                style={{ display: 'none' }}
                accept={Object.values(acceptedFileTypes).flat().join(',')}
                onChange={handleFileInputChange}
            />

            <div className="grid">
                {shouldShowUploadArea && (
                    <div className="col-12">
                        <label className="block mb-2 font-bold">{th('fileUpload.uploadChangeFile')}</label>
                        <div {...getRootProps({
                            className: 'dropzone flex align-items-center justify-content-center p-3 border-2 border-dashed border-primary cursor-pointer text-center',
                            style: { minHeight: '120px' }
                        })}>
                            <input {...getInputProps()} />
                            <div className="text-center">
                                <i className="pi pi-cloud-upload text-4xl text-primary mb-2"></i>
                                <p className="text-gray-600 m-0">{dropzoneMessage}</p>
                                <small className="text-gray-500">
                                    {th('fileUpload.maxFileSize', { maxSize: maxFileSize / 1024 / 1024 })}
                                    {!multiple && <span className="block">{th('fileUpload.singleFileOnly')}</span>}
                                </small>
                                <small className="text-gray-400 block mt-1">
                                    {getSupportedFormatsText()}
                                </small>
                            </div>
                        </div>

                        {newFiles.length > 0 && (
                            <div className="mt-3">
                                <label className="block mb-2 font-bold text-green-600">
                                    {multiple
                                        ? th('fileUpload.filesToUpload', { count: newFiles.length })
                                        : th('fileUpload.fileToUpload')
                                    }
                                </label>
                                <div className="p-3 border-1 surface-border border-round bg-green-50">
                                    {newFiles.map((file, index) => (
                                        <div key={index} className="flex align-items-center justify-content-between p-2 mb-2 bg-white border-round">
                                            <div className="flex align-items-center flex-1" style={{ minWidth: 0 }}>
                                                <i className={`${getFileIcon(file.name)} mr-2 text-green-600`}></i>
                                                <div className="flex-1">
                                                    <div className="font-medium text-ellipsis overflow-hidden whitespace-nowrap" title={file.name}>
                                                        {file.name}
                                                    </div>
                                                    <small className="text-gray-500">{formatFileSize(file.size)}</small>
                                                </div>
                                            </div>
                                            <Button
                                                type="button"
                                                icon="pi pi-times"
                                                className="p-button-rounded p-button-text p-button-danger p-button-sm"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    removeNewFile(index);
                                                }}
                                                tooltip={th('fileUpload.removeFile')}
                                                tooltipOptions={{ position: 'top' }}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
                {/* Mevcut dosyalar bölümü */}
                {existingDocuments.length > 0 || isLoading ? (
                    <div className="col-12 mt-3">
                        <label className="block mb-2 font-bold">
                            {readOnlyMode ? th('fileUpload.files') : th('fileUpload.uploadedFiles')}
                        </label>
                        <div className="p-3 border-1 surface-border border-round" style={{ minHeight: '100px' }}>
                            {isLoading ? (
                                <div className="flex justify-content-center align-items-center h-full">
                                    <ProgressSpinner style={{ width: '30px', height: '30px' }} />
                                </div>
                            ) : (
                                <div className="grid">
                                    {existingDocuments.map((doc) => (
                                        <div key={doc.id} className="col-12 md:col-6 lg:col-4">
                                            <div className="p-3 border-1 surface-border border-round hover:surface-100 transition-colors transition-duration-150">
                                                <div className="flex align-items-center justify-content-between mb-2">
                                                    <i className={`${getFileIcon(doc.fileName)} text-2xl text-primary`}></i>
                                                    <div className="flex gap-1">
                                                        {/* Download Button - Form submit'ini engelle */}
                                                        {showDownloadButton && onDownloadDocument && (
                                                            <Button
                                                                type="button"
                                                                icon="pi pi-download"
                                                                className="p-button-rounded p-button-text p-button-sm"
                                                                onClick={(e) => handleDownloadDocument(doc, e)}
                                                                tooltip={th('fileUpload.download')}
                                                                tooltipOptions={{ position: 'top' }}
                                                            />
                                                        )}
                                                        {/* Update Button - Form submit'ini engelle */}
                                                        {showUpdateButton && onUpdateExistingDocument && !readOnlyMode && (
                                                            <Button
                                                                type="button"
                                                                icon="pi pi-refresh"
                                                                className="p-button-rounded p-button-text p-button-warning p-button-sm"
                                                                onClick={(e) => handleUpdateDocument(doc, e)}
                                                                tooltip={th('fileUpload.update')}
                                                                tooltipOptions={{ position: 'top' }}
                                                            />
                                                        )}
                                                        {/* Delete Button - Form submit'ini engelle */}
                                                        {showDeleteButton && onDeleteExistingDocument && !readOnlyMode && (
                                                            <Button
                                                                type="button"
                                                                icon="pi pi-trash"
                                                                className="p-button-rounded p-button-text p-button-danger p-button-sm"
                                                                onClick={(e) => handleDeleteDocument(doc, e)}
                                                                tooltip={th('fileUpload.delete')}
                                                                tooltipOptions={{ position: 'top' }}
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="text-sm font-medium mb-1 whitespace-nowrap overflow-hidden text-ellipsis" title={doc.fileName}>
                                                    {doc.fileName}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                ) : null}
            </div>
        </>
    );
};

export default GenericFileUpload;
